import React, { useState } from "react";

import { Link } from "react-router-dom";

import {
  Linkedin,
  Instagram,
  Facebook,
  Mail,
  Phone,
  MapPin,
  ArrowRight,
} from "lucide-react";

/* LOGO */

import logo from "@/assets/companylogo.png";

import {
  COMPANY,
  SERVICES,
  api,
} from "@/lib/site";

import { toast } from "sonner";

export default function Footer() {

  const [email, setEmail] = useState("");

  const [submitting, setSubmitting] =
    useState(false);

  const subscribe = async (e) => {

    e.preventDefault();

    if (!email) return;

    setSubmitting(true);

    try {

      const { data } = await api.post(
        "/newsletter",
        { email }
      );

      toast.success(
        data.message || "Subscribed!"
      );

      setEmail("");

    } catch {

      toast.error(
        "Subscription failed. Try again."
      );

    } finally {

      setSubmitting(false);

    }
  };

  return (
    <footer
      className="
        relative
        mt-20 sm:mt-24
        overflow-hidden

        border-t border-purple-200/40

        bg-gradient-to-b
        from-[#FAF7FC]
        via-white
        to-[#F5ECF8]
      "
    >
      {/* BACKGROUND GLOW */}

      <div
        className="
          absolute top-0 right-0

          w-[300px] sm:w-[500px]
          h-[300px] sm:h-[500px]

          bg-purple-300/20

          blur-[140px]

          rounded-full
        "
      />

      <div
        className="
          absolute bottom-0 left-0

          w-[250px] sm:w-[400px]
          h-[250px] sm:h-[400px]

          bg-fuchsia-300/10

          blur-[120px]

          rounded-full
        "
      />

      {/* FLOATING GLOW */}

      <div
        className="
          absolute top-32 left-10
          w-32 h-32
          bg-purple-400/10
          blur-3xl
          rounded-full
          animate-pulse
          hidden lg:block
        "
      />

      <div
        className="
          relative
          max-w-7xl
          mx-auto

          px-4 sm:px-6 md:px-8

          py-16 sm:py-20
        "
      >
        {/* TOP GRID */}

        <div
          className="
            grid
            grid-cols-1
            md:grid-cols-12

            gap-12
          "
        >
          {/* BRAND */}

          <div className="md:col-span-4">
            <Link
              to="/"
              className="inline-flex items-center"
            >
              <img
                src={logo}
                alt="CodeFix Solutions"

                className="
                  h-14 sm:h-16 md:h-20

                  w-auto
                  object-contain

                  drop-shadow-[0_10px_30px_rgba(120,70,180,0.18)]

                  transition-all duration-300

                  hover:scale-[1.03]
                "
              />
            </Link>

            {/* TAGLINE */}

            <p
              className="
                mt-5 sm:mt-6

                text-slate-600
                leading-relaxed

                text-sm sm:text-base

                max-w-sm
              "
            >
              {COMPANY.tagline}. Premium IT services,
              AI solutions, staffing, consulting,
              cloud engineering, and digital
              transformation for modern businesses.
            </p>

            {/* SOCIALS */}

            <div
              className="
                flex items-center gap-3 sm:gap-4
                mt-6 sm:mt-7
              "
            >
              {[
                {
                  icon: Linkedin,
                  href: "https://www.linkedin.com/company/codefixsolutions/",
                },
                {
                  icon: Instagram,
                  href: "https://www.instagram.com/invites/contact/?igsh=15gy7ojj25w1p&utm_content=7anxnnj",
                },
                {
                  icon: Facebook,
                  href: "https://www.facebook.com/share/1BAW1Xort8/",
                },
              ].map((item, index) => {

                const Icon = item.icon;

                return (
                  <a
                    key={index}

                    href={item.href}

                    target="_blank"
                    rel="noopener noreferrer"

                    className="
                      group

                      w-10 h-10
                      sm:w-11 sm:h-11

                      rounded-2xl

                      bg-white/80
                      backdrop-blur-md

                      border border-purple-200/50

                      flex items-center justify-center

                      text-slate-600

                      shadow-sm

                      hover:bg-gradient-to-r
                      hover:from-[#6D3B8C]
                      hover:to-[#B86BC5]

                      hover:text-white

                      hover:shadow-lg
                      hover:shadow-purple-500/25

                      hover:-translate-y-1

                      transition-all duration-300
                    "
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* QUICK LINKS */}

          <div className="md:col-span-2">
            <h4
              className="
                font-semibold
                text-slate-900
                mb-5 sm:mb-6
              "
            >
              Quick Links
            </h4>

            <ul className="space-y-3 sm:space-y-4">
              {[
                ["Home", "/"],
                ["About", "/about"],
                ["Services", "/services"],
                ["Careers", "/careers"],
                ["Contact", "/contact"],
              ].map(([label, path]) => (
                <li key={label}>
                  <Link
                    to={path}

                    className="
                      text-slate-600
                      text-sm sm:text-base

                      hover:text-purple-700

                      transition-colors duration-300
                    "
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* SERVICES */}

          <div className="md:col-span-3">
            <h4
              className="
                font-semibold
                text-slate-900
                mb-5 sm:mb-6
              "
            >
              Services
            </h4>

            <ul className="space-y-3 sm:space-y-4">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}

                    className="
                      text-slate-600
                      text-sm sm:text-base

                      hover:text-purple-700

                      transition-colors duration-300
                    "
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* CONTACT */}

          <div className="md:col-span-3">
            <h4
              className="
                font-semibold
                text-slate-900
                mb-5 sm:mb-6
              "
            >
              Contact
            </h4>

            <div className="space-y-4 sm:space-y-5">
              <div className="flex items-start gap-3 text-slate-600 text-sm sm:text-base">
                <Mail className="w-4 h-4 text-purple-600 shrink-0 mt-1" />

                <span className="break-all">
                  codefixsolutions01@gmail.com
                </span>
              </div>

              <div className="flex items-center gap-3 text-slate-600 text-sm sm:text-base">
                <Phone className="w-4 h-4 text-purple-600 shrink-0" />

                +91 7075392825
              </div>

              <div className="flex items-center gap-3 text-slate-600 text-sm sm:text-base">
                <MapPin className="w-4 h-4 text-purple-600 shrink-0" />

                Hyderabad, India
              </div>
            </div>
          </div>
        </div>

        {/* BOTTOM */}

        <div
          className="
            mt-14 sm:mt-16
            pt-8

            border-t border-purple-200/40

            grid grid-cols-1
            md:grid-cols-2

            gap-8
          "
        >
          {/* NEWSLETTER */}

          <form
            onSubmit={subscribe}

            className="
              flex flex-col
              sm:flex-row

              gap-3

              max-w-md
            "
          >
            <input
              type="email"

              required

              value={email}

              onChange={(e) =>
                setEmail(e.target.value)
              }

              placeholder="Subscribe to newsletter"

              className="
                flex-1

                px-5 py-3.5

                rounded-2xl

                bg-white/80
                backdrop-blur-md

                border border-purple-200

                outline-none

                text-sm sm:text-base

                focus:border-purple-500
                focus:ring-4
                focus:ring-purple-200/40

                transition-all
              "
            />

            <button
              type="submit"

              disabled={submitting}

              className="
                h-[54px]
                sm:w-[54px]

                flex items-center justify-center

                rounded-2xl

                bg-gradient-to-r
                from-[#6D3B8C]
                via-[#9B4D96]
                to-[#B86BC5]

                text-white

                shadow-lg
                shadow-purple-500/20

                hover:scale-105
                active:scale-[0.96]

                hover:shadow-purple-500/35

                transition-all duration-300
              "
            >
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          {/* COPYRIGHT */}

          <div
            className="
              text-sm text-slate-500

              flex flex-col
              sm:flex-row

              md:justify-end

              gap-3 sm:gap-4
            "
          >
            <span>
              © {new Date().getFullYear()} CodeFix Solutions
            </span>

            <a
              href="#"

              className="
                hover:text-purple-700
                transition-colors duration-300
              "
            >
              Privacy Policy
            </a>

            <a
              href="#"

              className="
                hover:text-purple-700
                transition-colors duration-300
              "
            >
              Terms
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}